import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User, Tenant, UserRole, AppMode } from '../types';
import { effectiveUiRole } from '../utils/roleLabels';
import { ApiClient } from '../api/client';
import { resetUserPrefs } from '../hooks/useUserPref';

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  role: UserRole;
  appMode: AppMode;
  isLoading: boolean;
  isImpersonating: boolean;
  login: (email: string, pass: string, loginToken?: string) => Promise<{ ok: boolean; error?: string; requires_2fa?: boolean; temp_token?: string }>;
  verify2FA: (tempToken: string, code: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  setAppMode: (mode: AppMode) => void;
  refreshUser: () => Promise<void>;
  impersonateTenant: (tenantId: number) => Promise<{ ok: boolean; error?: string }>;
  stopImpersonation: () => Promise<void>;
  isSandbox: boolean;
  enterSandbox: (tenantId?: number) => Promise<{ ok: boolean; error?: string }>;
  exitSandbox: () => Promise<void>;
  resetSandbox: () => Promise<{ ok: boolean; error?: string; message?: string }>;
  promoteSandbox: (recordIds?: number[]) => Promise<{ ok: boolean; error?: string; promoted_count?: number }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('kf_orig_admin_token'));
  });
  const [isSandbox, setIsSandbox] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('kf_sandbox_orig_token'));
  });
  const [appMode, setAppModeState] = useState<AppMode>(() => {
    return (localStorage.getItem('kf_app_mode') as AppMode) || 'simplified';
  });

  const setAppMode = (mode: AppMode) => {
    setAppModeState(mode);
    localStorage.setItem('kf_app_mode', mode);
  };

  const refreshUser = async () => {
    if (!ApiClient.getToken()) {
      setUser(null);
      setTenant(null);
      setIsLoading(false);
      return;
    }

    try {
      const res = await ApiClient.me();
      if (res.ok && res.user) {
        setUser(res.user);
        setTenant(res.tenant);
        if (res.tenant?.is_demo && !isSandbox) {
          setIsSandbox(true);
        }
      } else {
        setUser(null);
        setTenant(null);
      }
    } catch (e) {
      console.error('Felhasználói adatok frissítési hiba:', e);
      setUser(null);
      setTenant(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();

    const handleExpired = () => {
      setUser(null);
      setTenant(null);
      localStorage.removeItem('kf_orig_admin_token');
      localStorage.removeItem('kf_sandbox_orig_token');
      localStorage.removeItem('kf_sandbox_orig_tenant_id');
      setIsImpersonating(false);
      setIsSandbox(false);
    };

    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, []);

  const login = async (email: string, pass: string, loginToken?: string) => {
    const res = await ApiClient.login(email, pass, loginToken);
    if (res.ok && res.requires_2fa) {
      return { ok: false, requires_2fa: true, temp_token: res.temp_token };
    }
    if (res.ok && res.user) {
      localStorage.removeItem('kf_orig_admin_token');
      localStorage.removeItem('kf_sandbox_orig_token');
      localStorage.removeItem('kf_sandbox_orig_tenant_id');
      setIsImpersonating(false);
      setIsSandbox(false);
      setUser(res.user);
      setTenant(res.tenant);
      return { ok: true };
    }
    return { ok: false, error: res.error || 'Sikertelen bejelentkezés' };
  };

  const verify2FA = async (tempToken: string, code: string) => {
    const res = await ApiClient.verify2FALogin(tempToken, code);
    if (res.ok && res.user) {
      localStorage.removeItem('kf_orig_admin_token');
      localStorage.removeItem('kf_sandbox_orig_token');
      localStorage.removeItem('kf_sandbox_orig_tenant_id');
      setIsImpersonating(false);
      setIsSandbox(false);
      setUser(res.user);
      setTenant(res.tenant);
      return { ok: true };
    }
    return { ok: false, error: res.error || 'Érvénytelen 2FA kód' };
  };

  const logout = async () => {
    localStorage.removeItem('kf_orig_admin_token');
    localStorage.removeItem('kf_sandbox_orig_token');
    localStorage.removeItem('kf_sandbox_orig_tenant_id');
    setIsImpersonating(false);
    setIsSandbox(false);
    await ApiClient.logout();
    resetUserPrefs();
    setUser(null);
    setTenant(null);
  };

  const impersonateTenant = async (tenantId: number) => {
    const origToken = ApiClient.getToken();
    if (!origToken) return { ok: false, error: 'Nincs bejelentkezett munkamenet' };

    const res = await ApiClient.impersonateTenant(tenantId);
    if (res.ok && res.token && res.user) {
      if (!localStorage.getItem('kf_orig_admin_token')) {
        localStorage.setItem('kf_orig_admin_token', origToken);
      }
      ApiClient.setToken(res.token);
      setUser(res.user);
      setTenant(res.tenant);
      setIsImpersonating(true);
      return { ok: true };
    }
    return { ok: false, error: res.error || 'Sikertelen megszemélyesítés' };
  };

  const stopImpersonation = async () => {
    const origToken = localStorage.getItem('kf_orig_admin_token');
    if (origToken) {
      localStorage.removeItem('kf_orig_admin_token');
      ApiClient.setToken(origToken);
      setIsImpersonating(false);
      await refreshUser();
    }
  };

  const enterSandbox = async (tenantId?: number) => {
    const origToken = ApiClient.getToken();
    if (!origToken) return { ok: false, error: 'Nincs bejelentkezett munkamenet' };

    const res = await ApiClient.enterSandbox(tenantId);
    if (res.ok && res.token && res.user && res.tenant) {
      if (!localStorage.getItem('kf_sandbox_orig_token')) {
        localStorage.setItem('kf_sandbox_orig_token', origToken);
        localStorage.setItem('kf_sandbox_orig_tenant_id', String(res.original_tenant_id));
      }
      ApiClient.setToken(res.token);
      setUser(res.user);
      setTenant(res.tenant);
      setIsSandbox(true);
      return { ok: true };
    }
    return { ok: false, error: res.error || 'Sikertelen belépés a Homokozóba' };
  };

  const exitSandbox = async () => {
    const origToken = localStorage.getItem('kf_sandbox_orig_token');
    if (origToken) {
      localStorage.removeItem('kf_sandbox_orig_token');
      localStorage.removeItem('kf_sandbox_orig_tenant_id');
      ApiClient.setToken(origToken);
      setIsSandbox(false);
      await refreshUser();
    } else {
      setIsSandbox(false);
      await refreshUser();
    }
  };

  const resetSandbox = async () => {
    const origTenantId = localStorage.getItem('kf_sandbox_orig_tenant_id')
      ? Number(localStorage.getItem('kf_sandbox_orig_tenant_id'))
      : undefined;
    const res = await ApiClient.resetSandbox(origTenantId);
    if (res.ok) {
      await refreshUser();
      return { ok: true, message: res.message };
    }
    return { ok: false, error: res.error || 'Sikertelen alaphelyzetbe állítás' };
  };

  const promoteSandbox = async (recordIds?: number[]) => {
    const targetTenantId = localStorage.getItem('kf_sandbox_orig_tenant_id')
      ? Number(localStorage.getItem('kf_sandbox_orig_tenant_id'))
      : undefined;
    const res = await ApiClient.promoteSandbox(targetTenantId, recordIds);
    if (res.ok) {
      return { ok: true, promoted_count: res.promoted_count };
    }
    return { ok: false, error: res.error || 'Sikertelen átvétel az élesbe' };
  };

  // A szerverrel azonos szabály: a granular_role csak szűkíthet (utils/roleLabels.effectiveUiRole)
  const getEffectiveRole = (u: any): UserRole => effectiveUiRole(u) as UserRole;

  const role: UserRole = getEffectiveRole(user);

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        role,
        appMode,
        isLoading,
        isImpersonating,
        isSandbox,
        login,
        verify2FA,
        logout,
        setAppMode,
        refreshUser,
        impersonateTenant,
        stopImpersonation,
        enterSandbox,
        exitSandbox,
        resetSandbox,
        promoteSandbox,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
